import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  AiFillHeart,
  AiOutlineHeart,
  AiOutlineMessage,
  AiOutlineShoppingCart,
  AiOutlinePlus,
  AiOutlineMinus,
  AiOutlineStar,
  AiFillStar,
} from "react-icons/ai";
import { HiOutlineTruck } from "react-icons/hi";
import { useDispatch, useSelector } from "react-redux";
import {
  getProductImageUrl,
  getProductVideoUrl,
  getAllProductMedia,
  getAvatarUrl,
} from "../../utils/mediaUtils";
import { getAllProductsShop } from "../../redux/actions/product";
import { backend_url, server } from "../../server";
import {
  addToWishlist,
  removeFromWishlist,
} from "../../redux/actions/wishlist";
import { addTocart } from "../../redux/actions/cart";
import { toast } from "react-toastify";
import Ratings from "./Ratings";
import FullScreenMediaViewer from "./FullScreenImageViewer";
import ReadMoreText from "../UI/ReadMoreText";
import axios from "axios";

import { useCurrency } from "../../context/CurrencyContext";
import OfferForm from "./OfferForm";

const ProductDetails = ({ data }) => {
  const { products } = useSelector((state) => state.products);
  const { user, isAuthenticated } = useSelector((state) => state.user);
  const { wishlist } = useSelector((state) => state.wishlist);
  const { cart } = useSelector((state) => state.cart);
  const dispatch = useDispatch();
  const { formatPrice } = useCurrency();

  // Check if current user is admin
  const isAdmin = user && user.role === "Admin";

  const [count, setCount] = useState(1);
  const [click, setClick] = useState(false);
  const [select, setSelect] = useState(0);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [modalClosing, setModalClosing] = useState(false);
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState("");
  const [userCanReview, setUserCanReview] = useState(false);
  const [hasExistingReview, setHasExistingReview] = useState(false);
  const [isFullScreenOpen, setIsFullScreenOpen] = useState(false);
  const [fullScreenIndex, setFullScreenIndex] = useState(0);

  const [showOfferForm, setShowOfferForm] = useState(false);

  // Vendor delivery config state (new district-based system)
  const [vendorDeliveryConfig, setVendorDeliveryConfig] = useState(null);
  const [deliveryConfigLoading, setDeliveryConfigLoading] = useState(false);

  // Attribute selection and price variation state
  const [selectedAttributes, setSelectedAttributes] = useState({});
  const [currentPrice, setCurrentPrice] = useState(null);

  // Combine images and videos into a single media array for viewer
  const allMedia = [...(data?.images || []), ...(data?.videos || [])];
  const navigate = useNavigate();

  const calculateCurrentPrice = (selectedAttrs) => {
    const fallbackPrice = data?.discountPrice || data?.originalPrice || data?.price || 0;
    if (!data || !data.attributes || data.attributes.length === 0) {
      return fallbackPrice;
    }

    let basePrice = fallbackPrice;
    let priceAdjustment = 0;

    // Check each selected attribute for price variations
    Object.entries(selectedAttrs).forEach(([attributeName, selectedValue]) => {
      const attribute = data.attributes.find(
        (attr) => attr.name === attributeName
      );
      if (attribute && attribute.hasPriceVariation) {
        const valueObj = attribute.values.find(
          (val) => (typeof val === "string" ? val : val.value) === selectedValue
        );
        if (valueObj && typeof valueObj === "object" && valueObj.price) {
          // Use the specific price for this attribute value
          priceAdjustment += valueObj.price - basePrice;
        }
      }
    });

    return basePrice + priceAdjustment;
  };

  // Handle attribute selection
  const handleAttributeSelect = (attributeName, value) => {
    const newSelectedAttributes = {
      ...selectedAttributes,
      [attributeName]: value,
    };
    setSelectedAttributes(newSelectedAttributes);
    setCurrentPrice(calculateCurrentPrice(newSelectedAttributes));
  };

  // Initialize current price when data loads
  useEffect(() => {
    if (data) {
      setCurrentPrice(data.discountPrice || data.originalPrice || data.price || 0);
    }
  }, [data]);

  useEffect(() => {
    dispatch(getAllProductsShop(data && data?.shop?._id));
    if (wishlist && wishlist.find((i) => i._id === data?._id)) {
      setClick(true);
    } else {
      setClick(false);
    }
  }, [data, wishlist, dispatch]);

  // Check if user can review this product (has purchased and received it)
  useEffect(() => {
    const checkUserEligibility = async () => {
      console.log("🔍 Checking eligibility:", {
        isAuthenticated,
        user: !!user,
        data: !!data,
      });

      if (!isAuthenticated || !user || !data?._id) {
        console.log("❌ Basic check failed");
        setUserCanReview(false);
        return;
      }

      try {
        console.log(
          "📞 Calling API:",
          `${server}/order/get-all-orders/${user._id}`
        );
        const response = await axios.get(
          `${server}/order/get-all-orders/${user._id}`,
          { withCredentials: true }
        );

        console.log("📦 Orders response:", response.data);
        const orders = response.data.orders || [];

        // Check if user has purchased and received the product
        const hasDeliveredOrder = orders.some(
          (order) =>
            order.status === "Delivered" &&
            order.cart.some((item) => item._id === data._id)
        );

        // Check if user has already reviewed this product
        const hasAlreadyReviewed =
          data.reviews &&
          data.reviews.some((review) => review.user._id === user._id);

        console.log("🎯 Eligibility results:", {
          totalOrders: orders.length,
          hasDeliveredOrder,
          hasAlreadyReviewed,
          canReview: hasDeliveredOrder,
        });

        // Update states
        setHasExistingReview(hasAlreadyReviewed);
        // For now, let's allow all authenticated users to review for testing
        setUserCanReview(true); // Changed from hasDeliveredOrder to true for testing
      } catch (error) {
        console.error("❌ Error checking user eligibility:", error);
        setUserCanReview(false);
      }
    };

    checkUserEligibility();
  }, [isAuthenticated, user, data]);

  // Fetch vendor delivery config for new district-based delivery system
  useEffect(() => {
    const fetchVendorDeliveryConfig = async () => {
      if (data) {
        setDeliveryConfigLoading(true);
        try {
          // Get the shop ID from the product
          const shopId =
            data.isSellerProduct && data.sellerShop
              ? typeof data.sellerShop === "object" && data.sellerShop !== null
                ? data.sellerShop._id
                : data.sellerShop
              : data?.shop?._id;

          if (shopId) {
            const response = await axios.get(
              `${server}/vendor-delivery/config/${shopId}`
            );
            if (response.data.success) {
              setVendorDeliveryConfig(response.data.config);
            }
          }
        } catch (error) {
          console.log("Vendor delivery config not found, using defaults");
          setVendorDeliveryConfig(null);
        } finally {
          setDeliveryConfigLoading(false);
        }
      }
    };

    fetchVendorDeliveryConfig();
  }, [data]);

  // Handle smooth modal closing
  const handleCloseModal = () => {
    setModalClosing(true);
    setTimeout(() => {
      setShowReviewModal(false);
      setModalClosing(false);
      setRating(0);
      setComment("");
    }, 200); // Match the animation duration
  };

  // Remove from wish list
  const removeFromWishlistHandler = (data) => {
    setClick(!click);
    dispatch(removeFromWishlist(data));
  };

  // add to wish list
  const addToWishlistHandler = (data) => {
    setClick(!click);
    dispatch(addToWishlist(data));
  };

  // Add to cart
  const addToCartHandler = (id) => {
    const isItemExists = cart && cart.find((i) => i._id === id);

    if (isItemExists) {
      toast.error("item already in cart!");
    } else {
      if (data.stock < 1) {
        toast.error("Product stock limited!");
      } else {
        const shopId = data.shopId || (data.isSellerProduct && data.sellerShop ? (typeof data.sellerShop === 'object' ? data.sellerShop._id : data.sellerShop) : data?.shop?._id);
        const cartData = {
          ...data,
          qty: count,
          shopId: shopId,
          selectedAttributes: selectedAttributes,
          finalPrice: currentPrice || data.discountPrice,
          attributeSelection:
            Object.keys(selectedAttributes).length > 0
              ? selectedAttributes
              : null,
        };
        dispatch(addTocart(cartData));
        toast.success("Item added to cart Successfully!");
      }
    }
  };

  // Buy Now - direct checkout
  const buyNowHandler = () => {
    if (!user) {
      toast.error("Please login as a user to continue!");
      return;
    }

    if (data.stock < count) {
      toast.error("Product stock limited!");
      return;
    }

    // Use currentPrice if available (with selected attributes), otherwise use discountPrice
    const baseProductPrice = data.discountPrice || data.originalPrice || data.price || 0;
    const itemPrice = currentPrice || baseProductPrice;

    const shopId = data.shopId || (data.isSellerProduct && data.sellerShop ? (typeof data.sellerShop === 'object' ? data.sellerShop._id : data.sellerShop) : data?.shop?._id);
    
    // Create order data with selected quantity and attributes
    const cartItem = {
      ...data,
      qty: count,
      shopId: shopId,
      selectedAttributes: selectedAttributes,
      finalPrice: currentPrice || baseProductPrice, // Include finalPrice if attributes affect pricing
      attributeSelection: Object.keys(selectedAttributes).length > 0 ? selectedAttributes : null,
    };

    const orderData = {
      cart: [cartItem],
      subTotalPrice: itemPrice * count,
      shipping: itemPrice * count * 0.1, // 10% shipping
      discountPrice: 0,
      totalPrice: itemPrice * count + itemPrice * count * 0.1,
      user: user,
    };

    // Store in localStorage and navigate to checkout
    localStorage.setItem("latestOrder", JSON.stringify(orderData));
    navigate("/checkout");
  };

  const incrementCount = () => {
    setCount(count + 1);
  };
  const decrementCount = () => {
    if (count > 1) {
      setCount(count - 1);
    }
  };

  // Handle review submission
  const handleReviewSubmit = async (e) => {
    e.preventDefault();

    if (!isAuthenticated || !user) {
      toast.error("Please login to submit a review");
      return;
    }

    if (!rating || rating === 0) {
      toast.error("Please select a rating");
      return;
    }

    if (!comment.trim()) {
      toast.error("Please write a review comment");
      return;
    }

    try {
      const reviewData = {
        user: user,
        rating: rating,
        comment: comment.trim(),
        productId: data._id,
        orderId: "direct", // Since this is from product page
      };

      await axios.put(`${server}/product/create-new-review`, reviewData, {
        withCredentials: true,
      });

      toast.success("Review submitted successfully!");

      // Trigger refresh of shop products to get updated reviews instantly
      dispatch(getAllProductsShop(data?.shop?._id));

      // Reset form and close modal smoothly
      handleCloseModal();

      // Hide the review button since user has now reviewed the product
      setUserCanReview(false);
    } catch (error) {
      console.error("Error submitting review:", error);
      toast.error(error.response?.data?.message || "Failed to submit review");
    }
  };

  const totalReviewsLength = data && data.reviews ? data.reviews.length : 0;

  // Calculate shop's average rating based on all products from this shop
  const shopProducts =
    (products &&
      products.filter((product) => product.shop?._id === data?.shop?._id)) ||
    [];

  let shopTotalRatings = 0;
  let productsWithReviewsCount = 0;

  shopProducts.forEach((product) => {
    if (product.reviews && product.reviews.length > 0) {
      const productAvg =
        product.reviews.reduce((sum, review) => sum + review.rating, 0) /
        product.reviews.length;
      shopTotalRatings += productAvg;
      productsWithReviewsCount++;
    }
  });

  const shopAverageRating =
    productsWithReviewsCount > 0
      ? (shopTotalRatings / productsWithReviewsCount).toFixed(1)
      : 0;

  // This is for the overall products rating calculation (not used for shop rating)
  // const totalRatings =
  //   products &&
  //   products.reduce(
  //     (acc, product) =>
  //       acc + product.reviews.reduce((sum, review) => sum + review.rating, 0),
  //     0
  //   );

  // const avg = totalRatings / totalReviewsLength || 0;

  // Sand message
  const handleMessageSubmit = async () => {
    if (isAuthenticated) {
      const groupTitle = data._id + user._id;
      const userId = user._id;
      const sellerId = data?.shop?._id || data?.shopId;
      await axios
        .post(`${server}/conversation/create-new-conversation`, {
          groupTitle,
          userId,
          sellerId,
        })
        .then((res) => {
          navigate(`/inbox?${res.data.conversation._id}`);
        })
        .catch((error) => {
          toast.error(error.response.data.message);
        });
    } else {
      toast.error("Please login as a user to create a conversation");
    }
  };

  const discountPercentage =
    data && data.originalPrice
      ? Math.round(
          ((data.originalPrice - data.discountPrice) / data.originalPrice) * 100
        )
      : 0;

  return (
    <div className="bg-white min-h-screen selection:bg-blue-100 selection:text-blue-900 pb-12 font-sans">
      {!data ? (
        <div className="max-w-2xl mx-auto py-24 text-center">
          <h2 className="text-2xl font-bold text-gray-800 mb-4">
            Product Not Found
          </h2>
          <p className="text-gray-600 mb-8">
            Sorry, we couldn't find the product you're looking for.
          </p>
          <Link
            to="/"
            className="inline-block px-6 py-3 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 transition-colors"
          >
            Go to Home
          </Link>
        </div>
      ) : (
        <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-12 py-8 lg:py-16">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-24">
            {/* Product Media (Images & Videos) */}
            <div className="space-y-6">
              <div className="bg-gray-50 rounded-[2rem] overflow-hidden cursor-pointer group transition-all duration-500">
                <div
                  className="relative aspect-[4/3]"
                  onClick={() => {
                    setFullScreenIndex(select);
                    setIsFullScreenOpen(true);
                  }}
                >
                  {/* Display current selected media (image or video) */}
                  {data && data.images && data.images[select] ? (
                    <img
                      src={getProductImageUrl(data.images, select, backend_url)}
                      alt={data.name}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                  ) : data &&
                    data.videos &&
                    data.videos[select - (data.images?.length || 0)] ? (
                    <video
                      className="w-full h-full object-cover"
                      controls
                      preload="metadata"
                    >
                      <source
                        src={getProductVideoUrl(
                          data.videos,
                          select - (data.images?.length || 0),
                          backend_url
                        )}
                        type="video/mp4"
                      />
                      Your browser does not support the video tag.
                    </video>
                  ) : (
                    <div className="w-full h-full flex items-center justify-center bg-gray-100">
                      <span className="text-gray-400">No media available</span>
                    </div>
                  )}

                  {data.originalPrice && (
                    <div className="absolute top-4 left-4">
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-red-100 text-red-800 shadow-sm">
                        {discountPercentage}% OFF
                      </span>
                    </div>
                  )}

                  {/* Click to zoom indicator */}
                  <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-10 transition-all duration-300 flex items-center justify-center">
                    <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-white/90 rounded-full p-3 shadow-lg">
                      <svg
                        className="w-6 h-6 text-gray-700"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7"
                        />
                      </svg>
                    </div>
                  </div>
                </div>
              </div>

              {/* Thumbnail Media (Images & Videos) */}
              <div className="flex space-x-2 overflow-x-auto pb-2">
                {/* Image Thumbnails */}
                {data &&
                  data.images &&
                  data.images.map((image, index) => (
                    <button
                      key={`img-${index}`}
                      className={`flex-shrink-0 w-16 h-16 rounded-lg overflow-hidden border-2 transition-all duration-200 relative ${
                        select === index
                          ? "border-blue-500 ring-2 ring-blue-200"
                          : "border-gray-200 hover:border-gray-300"
                      }`}
                      onClick={() => setSelect(index)}
                      onDoubleClick={() => {
                        setSelect(index);
                        setFullScreenIndex(index);
                        setIsFullScreenOpen(true);
                      }}
                      title="Click to select, double-click to view fullscreen"
                    >
                      <img
                        src={getProductImageUrl(
                          data.images,
                          index,
                          backend_url
                        )}
                        alt={`${data.name} ${index + 1}`}
                        className="w-full h-full object-cover"
                      />
                    </button>
                  ))}

                {/* Video Thumbnails */}
                {data &&
                  data.videos &&
                  data.videos.map((video, index) => {
                    const videoIndex = (data.images?.length || 0) + index;
                    return (
                      <button
                        key={`vid-${index}`}
                        className={`flex-shrink-0 w-16 h-16 rounded-lg overflow-hidden border-2 transition-all duration-200 relative ${
                          select === videoIndex
                            ? "border-purple-500 ring-2 ring-purple-200"
                            : "border-gray-200 hover:border-gray-300"
                        }`}
                        onClick={() => setSelect(videoIndex)}
                        onDoubleClick={() => {
                          setSelect(videoIndex);
                          setFullScreenIndex(videoIndex);
                          setIsFullScreenOpen(true);
                        }}
                        title="Click to select video, double-click to view fullscreen"
                      >
                        <video
                          className="w-full h-full object-cover"
                          preload="metadata"
                        >
                          <source
                            src={getProductVideoUrl(
                              data.videos,
                              index,
                              backend_url
                            )}
                            type="video/mp4"
                          />
                        </video>
                        {/* Video indicator */}
                        <div className="absolute inset-0 bg-black bg-opacity-30 flex items-center justify-center">
                          <svg
                            className="w-4 h-4 text-white"
                            fill="currentColor"
                            viewBox="0 0 20 20"
                          >
                            <path
                              fillRule="evenodd"
                              d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z"
                              clipRule="evenodd"
                            />
                          </svg>
                        </div>
                      </button>
                    );
                  })}
              </div>
            </div>
            {/* Product Information */}
            <div className="space-y-8 flex flex-col justify-start max-w-xl mx-auto lg:mx-0 w-full pt-4 lg:pt-10">
              <div>
                <h1 className="text-3xl lg:text-5xl font-bold text-gray-900 leading-[1.1] tracking-tight mb-4">
                  {data.name}
                </h1>

                <div className="mt-4">
                  <ReadMoreText
                    text={data.description}
                    maxLength={300}
                    readMoreClassName="text-base text-gray-500 leading-relaxed"
                    buttonClassName="text-blue-600 hover:text-blue-700 font-medium ml-1"
                  />
                </div>
              </div>
              
              {/* Price Section */}
              <div className="flex items-baseline space-x-3">
                <span className="text-4xl font-semibold text-gray-900 tracking-tight">
                  {formatPrice(currentPrice || data.discountPrice)}
                </span>
                {data.originalPrice && (
                  <span className="text-lg text-gray-400 line-through">
                    {formatPrice(data.originalPrice)}
                  </span>
                )}
                {currentPrice && currentPrice !== data.discountPrice && (
                  <span className="text-sm text-blue-600 bg-blue-50 px-3 py-1 rounded-full font-medium ml-4">
                    Price updated
                  </span>
                )}
              </div>

              {/* Product Attributes Section - Flipkart Style */}
              {data && data.attributes && data.attributes.length > 0 ? (
                <div className="space-y-4">
                  <h4 className="text-lg font-semibold text-gray-900 mb-3">
                    Product Specifications
                  </h4>
                  {data.attributes.map((attr, index) => {
                    // Handle backward compatibility - if attr has 'value', convert to 'values' array
                    const values =
                      attr.values || (attr.value ? [attr.value] : []);
                    console.log("Attribute:", attr.name, "Values:", values);

                    return (
                      <div key={index} className="space-y-2">
                        <div className="flex items-center space-x-2">
                          <span className="text-sm font-medium text-gray-700 capitalize">
                            {attr.name}:
                          </span>
                        </div>

                        {/* Different display based on attribute type */}
                        {attr.type === "color" ? (
                          <div className="flex flex-wrap gap-2">
                            {values &&
                              values.map((valueObj, valueIndex) => {
                                const value =
                                  typeof valueObj === "string"
                                    ? valueObj
                                    : valueObj.value;
                                const price =
                                  typeof valueObj === "object"
                                    ? valueObj.price
                                    : null;
                                const isSelected =
                                  selectedAttributes[attr.name] === value;

                                return (
                                  <button
                                    key={valueIndex}
                                    onClick={() =>
                                      handleAttributeSelect(attr.name, value)
                                    }
                                    className={`flex items-center space-x-2 p-2 border-2 rounded-lg bg-white hover:bg-gray-50 transition-all ${
                                      isSelected
                                        ? "border-blue-500 bg-blue-50"
                                        : "border-gray-300"
                                    }`}
                                  >
                                    <div
                                      className="w-6 h-6 rounded-full border-2 border-gray-300 shadow-sm"
                                      style={{
                                        backgroundColor: value.toLowerCase(),
                                      }}
                                      title={value}
                                    ></div>
                                    <div className="text-left">
                                      <span className="text-sm text-gray-600 capitalize block">
                                        {value}
                                      </span>
                                      {price && attr.hasPriceVariation && (
                                        <span className="text-xs text-blue-600 font-medium">
                                          {formatPrice(price)}
                                        </span>
                                      )}
                                    </div>
                                  </button>
                                );
                              })}
                          </div>
                        ) : attr.type === "size" ||
                          attr.type === "memory" ||
                          attr.name.toLowerCase().includes("ram") ||
                          attr.name.toLowerCase().includes("storage") ? (
                          <div className="flex flex-wrap gap-2">
                            {values &&
                              values.map((valueObj, valueIndex) => {
                                const value =
                                  typeof valueObj === "string"
                                    ? valueObj
                                    : valueObj.value;
                                const price =
                                  typeof valueObj === "object"
                                    ? valueObj.price
                                    : null;
                                const isSelected =
                                  selectedAttributes[attr.name] === value;

                                return (
                                  <button
                                    key={valueIndex}
                                    onClick={() =>
                                      handleAttributeSelect(attr.name, value)
                                    }
                                    className={`px-4 py-2 border-2 text-sm font-medium rounded-lg hover:bg-blue-100 transition-colors ${
                                      isSelected
                                        ? "border-blue-500 bg-blue-100 text-blue-700"
                                        : "border-blue-300 bg-blue-50 text-blue-700 hover:border-blue-500"
                                    }`}
                                  >
                                    <div className="text-center">
                                      <div>{value}</div>
                                      {price && attr.hasPriceVariation && (
                                        <div className="text-xs text-blue-600 font-medium">
                                          {formatPrice(price)}
                                        </div>
                                      )}
                                    </div>
                                  </button>
                                );
                              })}
                          </div>
                        ) : attr.type === "boolean" ? (
                          <div className="flex flex-wrap gap-2">
                            {values &&
                              values.map((valueObj, valueIndex) => {
                                const value =
                                  typeof valueObj === "string" ||
                                  typeof valueObj === "boolean"
                                    ? valueObj
                                    : valueObj.value;

                                return (
                                  <span
                                    key={valueIndex}
                                    className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                                      value === "true" || value === true
                                        ? "bg-green-100 text-green-800"
                                        : "bg-gray-100 text-gray-800"
                                    }`}
                                  >
                                    {value === "true" || value === true
                                      ? "✓ Yes"
                                      : "✗ No"}
                                  </span>
                                );
                              })}
                          </div>
                        ) : (
                          <div className="flex flex-wrap gap-2">
                            {values &&
                              values.map((valueObj, valueIndex) => {
                                const value =
                                  typeof valueObj === "string"
                                    ? valueObj
                                    : valueObj.value;
                                const price =
                                  typeof valueObj === "object"
                                    ? valueObj.price
                                    : null;

                                return (
                                  <div
                                    key={valueIndex}
                                    className="flex items-center space-x-2 px-3 py-2 bg-gray-50 rounded-lg border"
                                  >
                                    <span className="text-sm text-gray-900 font-medium">
                                      {value}
                                    </span>
                                    {price && attr.hasPriceVariation && (
                                      <span className="text-xs text-blue-600 font-medium">
                                        {formatPrice(price)}
                                      </span>
                                    )}
                                    {attr.type && attr.type !== "text" && (
                                      <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">
                                        {attr.type}
                                      </span>
                                    )}
                                  </div>
                                );
                              })}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="space-y-2">
                  <p className="text-sm text-gray-500">
                    No specifications available for this product.
                  </p>
                </div>
              )}

              {/* Delivery Options Display - New District-Based System */}
              <div className="py-4 border-t border-b border-gray-100">
                <div className="flex items-start space-x-3">
                  <HiOutlineTruck className="w-5 h-5 text-gray-900 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <h4 className="font-medium text-gray-900 text-sm">
                      Delivery & Pickup
                    </h4>
                    {deliveryConfigLoading ? (
                      <div className="flex items-center mt-2">
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-900 mr-2"></div>
                        <span className="text-xs text-gray-600">
                          Checking availability...
                        </span>
                      </div>
                    ) : (
                      <div className="mt-2 space-y-2">
                        <div className="flex items-center space-x-2 text-sm text-gray-600">
                          <span className="text-gray-900 font-medium">🏪 Pickup</span>
                          <span>• Free in-store collection</span>
                        </div>
                        {vendorDeliveryConfig?.offersDelivery !== false && (
                          <div className="flex items-center space-x-2 text-sm text-gray-600">
                            <span className="text-gray-900 font-medium">🚚 Delivery</span>
                            <span>
                              • Available to selected districts
                              {vendorDeliveryConfig?.districtFees?.length > 0 &&
                                ` (${
                                  vendorDeliveryConfig.districtFees.filter(
                                    (d) => d.isAvailable
                                  ).length
                                })`}
                            </span>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Quantity and Actions */}
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <span className="text-sm font-medium text-gray-700">
                      Quantity:
                    </span>
                    <div className="flex items-center border border-gray-300 rounded-lg overflow-hidden">
                      <button
                        className="flex items-center justify-center w-8 h-8 text-gray-600 hover:bg-gray-100 transition-colors"
                        onClick={decrementCount}
                        disabled={count <= 1}
                      >
                        <AiOutlineMinus className="w-3 h-3" />
                      </button>
                      <span className="flex items-center justify-center w-10 h-8 bg-gray-50 text-gray-900 font-medium text-sm">
                        {count}
                      </span>
                      <button
                        className="flex items-center justify-center w-8 h-8 text-gray-600 hover:bg-gray-100 transition-colors"
                        onClick={incrementCount}
                      >
                        <AiOutlinePlus className="w-3 h-3" />
                      </button>
                    </div>
                  </div>
                  <button
                    onClick={() =>
                      click
                        ? removeFromWishlistHandler(data)
                        : addToWishlistHandler(data)
                    }
                    className={`flex items-center justify-center w-8 h-8 rounded-full border transition-all duration-200 ${
                      click
                        ? "border-red-500 bg-red-50 text-red-600 hover:bg-red-100"
                        : "border-gray-300 text-gray-600 hover:border-red-300 hover:text-red-600"
                    }`}
                    title={click ? "Remove from wishlist" : "Add to wishlist"}
                  >
                    {click ? (
                      <AiFillHeart className="w-4 h-4" />
                    ) : (
                      <AiOutlineHeart className="w-4 h-4" />
                    )}
                  </button>
                </div>
                <div className="space-y-3">
                  {/* Top Row: Buy Now and Add to Cart */}
                  <div className="flex flex-col sm:flex-row gap-4 mt-6">
                    <button
                      onClick={buyNowHandler}
                      className="flex-1 flex items-center justify-center px-6 py-4 font-semibold rounded-full focus:outline-none transition-all duration-200 text-base bg-blue-600 text-white hover:bg-blue-700"
                    >
                      Buy Now
                    </button>
                    <button
                      onClick={() => addToCartHandler(data._id)}
                      className="flex-1 flex items-center justify-center px-6 py-4 font-semibold rounded-full focus:outline-none transition-all duration-200 text-base bg-gray-900 text-white hover:bg-black"
                    >
                      Add to Bag
                    </button>
                  </div>

                  {/* Make an Offer — only shown when vendor allows negotiation */}
                  {data?.allowNegotiation && (
                    <div>
                      <button
                        onClick={() => setShowOfferForm((v) => !v)}
                        className="w-full flex items-center justify-center px-4 py-2.5 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-600 transition-all duration-200 text-sm shadow-sm"
                      >
                        {showOfferForm ? "Hide Offer Form" : "Make an Offer"}
                      </button>
                      {showOfferForm && (
                        <div className="mt-2">
                          <OfferForm
                            product={data}
                            onClose={() => setShowOfferForm(false)}
                          />
                        </div>
                      )}
                    </div>
                  )}

                  {/* Bottom Row: Write Review, Send Message, and Call Buttons */}
                  <div className="flex flex-col sm:flex-row gap-4 mt-2">
                    {/* Write Review Button */}
                    {isAuthenticated && userCanReview ? (
                      <button
                        onClick={() => {
                          // Pre-fill existing review data if editing
                          if (hasExistingReview && data.reviews) {
                            const existingReview = data.reviews.find(
                              (review) => review.user._id === user._id
                            );
                            if (existingReview) {
                              setRating(existingReview.rating);
                              setComment(existingReview.comment);
                            }
                          }
                          setShowReviewModal(true);
                        }}
                        className="flex-1 flex items-center justify-center px-4 py-3 bg-green-600 text-white font-semibold rounded-xl hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-600 transition-all duration-300 text-sm shadow-sm hover:-translate-y-0.5 min-h-[48px]"
                      >
                        <AiFillStar className="w-4 h-4 mr-2" />
                        {hasExistingReview ? "Edit Review" : "Write Review"}
                      </button>
                    ) : (
                      <div className="flex-1"></div>
                    )}

                    {/* Send Message Button */}
                    <button
                      onClick={handleMessageSubmit}
                      className="flex-1 flex items-center justify-center px-4 py-3 bg-purple-600 text-white font-semibold rounded-xl hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-600 transition-all duration-300 text-sm shadow-sm hover:-translate-y-0.5 min-h-[48px]"
                      title="Send Message"
                    >
                      <AiOutlineMessage className="w-4 h-4 mr-2" />
                      <span className="hidden sm:inline">Send Message</span>
                      <span className="sm:hidden">Message</span>
                    </button>
                  </div>


                </div>
              </div>

            </div>
          </div>

          {/* Product Details Info */}
          <ProductDetailsInfo
            data={data}
            products={products}
            totalReviewsLength={totalReviewsLength}
            averageRating={shopAverageRating}
          />
        </div>
      )}

      {/* Review Modal */}
      {showReviewModal && (
        <div
          className={`fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 ${
            modalClosing ? "animate-fadeOut" : "animate-fadeIn"
          }`}
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              handleCloseModal();
            }
          }}
        >
          <div
            className={`bg-white rounded-lg p-6 w-full max-w-md mx-4 shadow-2xl ${
              modalClosing ? "animate-modalSlideOut" : "animate-modalSlideIn"
            }`}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-900">
                Write a Review
              </h3>
              <button
                onClick={handleCloseModal}
                className="text-gray-400 hover:text-gray-600 transition-all duration-200 hover:bg-gray-100 rounded-full p-2 hover:rotate-90 transform"
              >
                
              </button>
            </div>

            <form onSubmit={handleReviewSubmit}>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Rating
                </label>
                <div className="flex items-center space-x-1">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <button
                      key={star}
                      type="button"
                      onClick={() => setRating(star)}
                      className={`text-2xl ${
                        star <= rating ? "text-yellow-400" : "text-gray-300"
                      } hover:text-yellow-400 transition-colors`}
                    >
                      
                    </button>
                  ))}
                  <span className="ml-2 text-sm text-gray-600">
                    {rating > 0 ? `${rating}/5` : "Select rating"}
                  </span>
                </div>
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Review
                </label>
                <textarea
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  rows={4}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Share your experience with this product..."
                  required
                />
              </div>

              <div className="flex space-x-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowReviewModal(false);
                    setRating(0);
                    setComment("");
                  }}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                >
                  Submit Review
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Full Screen Media Viewer */}
      <FullScreenMediaViewer
        media={allMedia}
        currentIndex={fullScreenIndex}
        isOpen={isFullScreenOpen}
        onClose={() => setIsFullScreenOpen(false)}
        productName={data?.name || "Product"}
      />
    </div>
  );
};

const ProductDetailsInfo = ({
  data,
  products,
  totalReviewsLength,
  averageRating,
}) => {
  const [active, setActive] = useState(1);

  const tabs = [
    { id: 1, label: "Product Details", icon: "📋" },
    { id: 2, label: "Product Reviews", icon: "⭐" },
  ];

  return (
    <div className="mt-8">
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        {/* Tab Navigation */}
        <div className="border-b border-gray-200">
          <nav className="flex space-x-6 px-4" aria-label="Tabs">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActive(tab.id)}
                className={`py-3 px-1 border-b-2 font-medium text-xs whitespace-nowrap transition-colors duration-200 ${
                  active === tab.id
                    ? "border-blue-500 text-blue-600"
                    : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                }`}
              >
                <span className="flex items-center space-x-1">
                  <span className="text-sm">{tab.icon}</span>
                  <span>{tab.label}</span>
                </span>
              </button>
            ))}
          </nav>
        </div>

        {/* Tab Content */}
        <div className="p-4">
          {active === 1 && (
            <div className="prose max-w-none">
              <h3 className="text-base font-semibold text-gray-900 mb-3">
                Product Description
              </h3>
              <ReadMoreText
                text={data.description}
                maxLength={500}
                readMoreClassName="text-sm text-gray-600 leading-normal"
                buttonClassName="hover:bg-primary-50 rounded-md"
              />
            </div>
          )}

          {active === 2 && (
            <div className="space-y-4">
              <h3 className="text-base font-semibold text-gray-900">
                Customer Reviews
              </h3>

              {data && data.reviews && data.reviews.length > 0 ? (
                <div className="space-y-6">
                  {data.reviews.map((item, index) => (
                    <div
                      key={index}
                      className="flex space-x-4 p-4 bg-gray-50 rounded-lg"
                    >
                      <img
                        src={getAvatarUrl(item.user.avatar, backend_url)}
                        alt={item.user.name}
                        className="w-12 h-12 rounded-full object-cover flex-shrink-0"
                      />
                      <div className="flex-1">
                        <div className="flex items-center justify-between mb-2">
                          <h4 className="font-medium text-gray-900">
                            {item.user.name}
                          </h4>
                          <Ratings rating={item.rating} />
                        </div>
                        <p className="text-gray-600">{item.comment}</p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <div className="w-16 h-16 mx-auto mb-4 bg-gray-100 rounded-full flex items-center justify-center">
                    <AiOutlineStar className="w-8 h-8 text-gray-400" />
                  </div>
                  <h3 className="text-lg font-medium text-gray-900 mb-2">
                    No reviews yet
                  </h3>
                  <p className="text-gray-500">
                    Be the first to review this product!
                  </p>
                </div>
              )}
            </div>
          )}

        </div>
      </div>
    </div>
  );
};

export default ProductDetails;
